import React from "react";
import { FlowConfiguration, FlowOutputType } from "../rule-engine-core/Flow/FlowConfiguration";
import styled from "styled-components";
import { FlowStepEditorRegistration } from "./Utilities/RegisterFlowEditor";
import type { BeforeCapture, DragStart, DragUpdate, DropResult } from "react-beautiful-dnd";
import { DragDropContext } from "react-beautiful-dnd";

import { AutoBind } from "../rule-engine-core/Common/AutoBind";
import { StateManager } from "../rule-engine-core/Common/StateManager";
import { ObserveState } from "../CommonUI/StateManagement/ObserveState";

import { FlowConstants } from "../rule-engine-core/Flow/Utilities/FlowConstants";
import { FlowEditContext } from "./FlowEditContext";
import { ObserveMultiStateProperties } from "../CommonUI/StateManagement/ObserveMultiStateProperties";
import { CandidateSequence } from "../rule-engine-core/Flow/Structure/CandidateSequence";
import { IInjectElem, IInjectTopItem, IInjectTopItemType, Topbar } from "../CommonUI/BuilderUI/Topbar";
import { Sidebar, SidebarItem } from "../CommonUI/BuilderUI/Sidebar";
import { ViewIcon } from "../CommonUI/Icon/ViewIcon";
import { ToolBox } from "./Builder/Components/ToolBox";
import { DataDefinition } from "../rule-engine-core/Common/Data/DataDefinition";
import { FlowDesignPage } from "./Builder/Components/XFlowEditor.Designer";
import { FlowBuilderPage } from "./Builder/FlowBuilderPage";
import { IPosition } from "./Builder/Common/IPosition";
import { FlowStepSequence } from "../rule-engine-core/Flow/Structure/FlowStepSequence";
import { FlowIcon } from "../CommonUI/Icon/FlowIcon";

import { TestDataIcon } from "../CommonUI/Icon/TestDataIcon";
import { BuilderEditPage } from "../CommonUI/BuilderUI/BuilderEditPage";
import { CodingIcon } from "../CommonUI/Icon/CodingIcon";
import { BaseFlowStepEditor } from "./Steps/BaseFlowStepEditor";
import { AppGlueError } from "../rule-engine-core/AppGlueError";
import { DataBuilder } from "../DataBuilder/DataBuilder";
import { UnitTestConfiguration } from "../rule-engine-core/Common/UnitTesting/UnitTestConfiguration";
import { DescriptionGrayIcon } from "../CommonUI/Icon/DescriptionGrayIcon";
import { OffDiv, OnDiv } from "./Builder/CommonStyles";
import { ErrorBranchIcon } from "../CommonUI/Icon/ErrorBranchIcon";
import { ExpandAllIcon } from "../CommonUI/Icon/ExpandAllIcon";
import { CollapseAllIcon } from "../CommonUI/Icon/CollapseAllIcon";
import { DataInputIcon } from "../CommonUI/Icon/DataInputIcon";
import { DataOutputIcon } from "../CommonUI/Icon/DataOutputIcon";
import { XExecutableTester } from "../CommonUI/Testing/XExecutableTester";
import { AppGlueCoreInit } from "../AppGlueCoreInit";
import { UIStyleConstants } from "../rule-engine-core/Common/UIStyleConstants";
import { TopbarViewButton } from "../CommonUI/CommonStyles/TopbarViewButton";

const FlowEditorDiv = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;

    *::-webkit-scrollbar-track {
        -webkit-box-shadow: inset 0 0 2px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        background-color: transparent;
    }

    *::-webkit-scrollbar {
        width: 8px;
        background-color: transparent;
    }

    *::-webkit-scrollbar-thumb {
        background-color: ${UIStyleConstants.BLUE_COLOR};
    }
`;

export interface FlowEditorParameters {
    flow: FlowConfiguration;
    flowEditContext?: FlowEditContext;
    flowStartDataDefinition?: DataDefinition;
    flowName?: string;
    onFlowNameUpdate?: (newName: string) => void;
    viewAPIUrl?: string;
    onFlowSave?: () => void;
    onFlowCancel?: () => void;
    saveUnitTest?: (unitTest: UnitTestConfiguration) => void;
    getUnitTests?: () => Promise<UnitTestConfiguration[]>;
}

export class FlowBuilder extends React.Component<FlowEditorParameters, {}> {
    editContext: FlowEditContext;
    mousePosition: IPosition = {
        x: 0,
        y: 0,
    };

    constructor(props: FlowEditorParameters) {
        super(props);

        AppGlueCoreInit.init();

        if (props.flowEditContext) {
            if (this.props.flowEditContext?.flow._id !== props.flow._id)
                throw new AppGlueError("flow id in context and flow do not match");
            this.editContext = props.flowEditContext;
        } else {
            this.editContext = new FlowEditContext(props.flow, this);
        }
        // ensure flow steps are registered
        AppGlueCoreInit.init();

        if (props.flowName) {
            this.editContext.flowName = props.flowName;
        }

        if (props.viewAPIUrl) {
            this.editContext.viewAPIUrl = props.viewAPIUrl;
        }

        if (props.onFlowSave) {
            this.editContext.onFlowSave = props.onFlowSave;
        }

        if (props.onFlowCancel) {
            this.editContext.onFlowCancel = props.onFlowCancel;
        }

        if (props.flowStartDataDefinition) {
            props.flow.inputData = props.flowStartDataDefinition;
        }

        if (props.flow.allSequences.length > 0) {
            for (let seq of [...props.flow.allSequences, props.flow.errorSequence, props.flow.endSequence]) {
                seq.flow = props.flow;
            }
        }

        window.addEventListener("mousemove", this.onMouseMove);
    }

    get flow(): FlowConfiguration {
        return this.props.flow;
    }

    onMouseMove = (event: MouseEvent) => {
        this.mousePosition = {
            x: event.clientX,
            y: event.clientY,
        };
    };

    componentWillUnmount() {
        window.removeEventListener("mousemove", this.onMouseMove);
    }

    getDefaultTabs(): SidebarItem[] {
        let sideBarTabs: SidebarItem[] = [];

        sideBarTabs.push(
            {
                value: FlowBuilderPage.Flow,
                renderUI: () => (
                    <>
                        <ObserveState
                            listenTo={this.editContext}
                            properties={[FlowStepSequence.DRAG_OVER]}
                            control={() => (
                                <ToolBox
                                    editContext={this.editContext}
                                    flow={this.flow}
                                />
                            )}
                        />

                        <ObserveMultiStateProperties
                            listeners={[
                                {
                                    listenTo: this.props.flow,
                                    propertyName: "sequences",
                                },
                                {
                                    listenTo: this.editContext,
                                    propertyName: "propName",
                                },
                            ]}
                            control={() => (
                                <FlowDesignPage
                                    flow={this.props.flow}
                                    editContext={this.editContext}
                                />
                            )}
                        />
                    </>
                ),
                icon: <FlowIcon />,
            },
            {
                value: FlowBuilderPage.Data,
                renderUI: () => {
                    return (
                        <DataBuilder
                            definition={this.props.flow.getInputDefinition()}
                            readOnly={!this.props.flow.canChangeInputs}
                        />
                    );
                },
                icon: <DataInputIcon />,
            },
        );

        if (this.props.flow.defineOutputs === FlowOutputType.DEFINED || !this.props.flow.canChangeOutputs) {
            sideBarTabs.push({
                value: FlowBuilderPage.OutputData,
                renderUI: () => {
                    return (
                        <DataBuilder
                            definition={this.props.flow.getExecutionResult()}
                            readOnly={!this.props.flow.canChangeOutputs}
                        />
                    );
                },
                icon: <DataOutputIcon />,
            });
        }

        sideBarTabs.push(
            {
                value: FlowBuilderPage.Configuration,
                renderUI: () => (
                    <BuilderEditPage
                        builder={this.props.flow}
                        updateCallback={() => {}}
                    />
                ),
                icon: <CodingIcon />,
            },
            {
                value: FlowBuilderPage.Test,
                renderUI: () => (
                    <XExecutableTester
                        saveUnitTest={this.props.saveUnitTest}
                        builderName={"Test"}
                        executable={this.props.flow}
                    />
                ),
                icon: <TestDataIcon />,
            },
        );

        return sideBarTabs;
    }

    @AutoBind
    onChangeFlowName(newName: string) {
        this.editContext.flowName = newName;
        StateManager.propertyChanged(this.editContext, FlowEditContext.FLOW_NAME);
        if (this.props.onFlowNameUpdate) this.props.onFlowNameUpdate(newName);
    }

    render() {
        let afterElems: (IInjectElem | JSX.Element)[] = [];
        if (this.props.viewAPIUrl) {
            afterElems.push(
                <TopbarViewButton
                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                    startIcon={<ViewIcon />}
                    href={this.editContext.viewAPIUrl}
                >
                    View API
                </TopbarViewButton>,
            );
        }
        return (
            <ObserveState
                listenTo={this}
                control={() => (
                    <DragDropContext
                        onBeforeCapture={this.onBeforeCapture}
                        onBeforeDragStart={this.onBeforeDragStart}
                        onDragEnd={this.onDragEnd}
                        onDragUpdate={this.onDragUpdate}
                    >
                        <FlowEditorDiv>
                            <ObserveState
                                listenTo={this.editContext}
                                properties={[
                                    FlowEditContext.FLOW_NAME,
                                    FlowEditContext.SELECTION_ELEMENT,
                                    FlowEditContext.IS_SHOW_DESCRIPTION,
                                    FlowEditContext.IS_SHOW_ERROR_BRANCH,
                                    FlowEditContext.IS_COLLAPSED,
                                ]}
                                control={() => {
                                    let afterItems: IInjectTopItem[] = [
                                        {
                                            label: "Show",
                                            type: IInjectTopItemType.ICONBUTTONGROUP,
                                            onSelect: (value: string) => {
                                                switch (value) {
                                                    case "description":
                                                        this.editContext.isShowDescription =
                                                            !this.editContext.isShowDescription;
                                                        break;
                                                    case "collapse":
                                                        this.editContext.isCollapsed = !this.editContext.isCollapsed;
                                                        break;
                                                }
                                            },
                                            items: [
                                                {
                                                    value: "description",
                                                    label: "Show / Hide All Description",
                                                    icon: <DescriptionGrayIcon />,
                                                    isSelected: () => {
                                                        return this.editContext.isShowDescription;
                                                    },
                                                    belowLabel: this.editContext.isShowDescription ? (
                                                        <OnDiv>on</OnDiv>
                                                    ) : (
                                                        <OffDiv>off</OffDiv>
                                                    ),
                                                },
                                                {
                                                    value: "error_branch",
                                                    label: "Show Error and End Connections",
                                                    icon: <ErrorBranchIcon />,
                                                    isSelected: () => {
                                                        return this.editContext.isShowErrorBranch;
                                                    },
                                                    belowLabel: this.editContext.isShowErrorBranch ? (
                                                        <OnDiv>on</OnDiv>
                                                    ) : (
                                                        <OffDiv>off</OffDiv>
                                                    ),
                                                    onMouseDown: () => {
                                                        this.editContext.isShowErrorBranch = true;
                                                    },
                                                    onMouseUp: () => {
                                                        this.editContext.isShowErrorBranch = false;
                                                    },
                                                },
                                                {
                                                    value: "collapse",
                                                    label: "Collapse / Expand all sequences",
                                                    icon: this.editContext.isCollapsed ? (
                                                        <ExpandAllIcon />
                                                    ) : (
                                                        <CollapseAllIcon />
                                                    ),
                                                    isSelected: () => {
                                                        return this.editContext.isCollapsed;
                                                    },
                                                    belowLabel: this.editContext.isCollapsed ? (
                                                        <OnDiv>on</OnDiv>
                                                    ) : (
                                                        <OffDiv>off</OffDiv>
                                                    ),
                                                },
                                            ],
                                            wrapperStyle: { border: "none" },
                                        },
                                    ];

                                    return (
                                        <Topbar
                                            name={this.editContext.flowName}
                                            onChangeName={this.onChangeFlowName}
                                            editContext={this.editContext}
                                            isActionDisabled={!this.editContext.lastSelectionElement}
                                            isRequiredActionGroup
                                            onSave={
                                                this.editContext.onFlowSave
                                                    ? () => {
                                                          if (this.editContext.onFlowSave) {
                                                              this.editContext.onFlowSave();
                                                          }
                                                      }
                                                    : undefined
                                            }
                                            onClose={this.editContext.onFlowCancel}
                                            afterItems={afterItems}
                                            afterElems={afterElems}
                                        />
                                    );
                                }}
                            />
                            <ObserveState
                                listenTo={this.editContext}
                                properties={[FlowEditContext.MODE, FlowConfiguration.OUTPUT_STRATEGY]}
                                control={() => (
                                    <Sidebar
                                        items={this.getDefaultTabs()}
                                        value={this.editContext.mode}
                                        onChange={(mode: string) => {
                                            Reflect.set(this.editContext, "mode", mode);
                                            StateManager.propertyChanged(this.editContext, FlowEditContext.MODE);
                                        }}
                                    />
                                )}
                            />
                        </FlowEditorDiv>
                    </DragDropContext>
                )}
            />
        );
    }

    @AutoBind
    onBeforeCapture(before: BeforeCapture) {
        this.editContext.draggingControl = {
            source: {
                current: before.draggableId,
            },
            pos: this.mousePosition,
        };
    }

    @AutoBind
    onBeforeDragStart(initial: DragStart) {
        this.editContext.clearSelection();
        this.editContext.draggingControl = {
            source: {
                parent: initial.source.droppableId,
                current: initial.draggableId,
            },
            pos: this.editContext.draggingControl!.pos,
        };
    }

    @AutoBind
    onDragUpdate(initial: DragUpdate) {
        this.editContext.draggingControl = {
            source: {
                parent: initial.source.droppableId,
                current: initial.draggableId,
            },
            destination: initial.destination?.droppableId,
            pos: this.editContext.draggingControl!.pos,
        };
    }

    @AutoBind
    onDragEnd(result: DropResult) {
        this.editContext.draggingControl = null;

        // if not destination... then we should not do anything
        if (!result.destination) {
            return;
        }

        // do not drag in toolbox
        if (result.destination.droppableId === FlowConstants.ToolboxId) return;

        // do not drag from normal sequences to error sequence, vice versa.
        if (
            (result.destination.droppableId === FlowConstants.ERROR_SEQUENCE &&
                result.source.droppableId !== FlowConstants.ToolboxId) ||
            result.source.droppableId === FlowConstants.ERROR_SEQUENCE
        ) {
            return;
        }

        let control: BaseFlowStepEditor | undefined;

        let registeredControl = FlowStepEditorRegistration.getByTypeName(result.draggableId);

        if (registeredControl) {
            control = registeredControl.create(this.editContext);
            this.editContext.registerStepEditor(control.getFlowStepData()._id, control);
        } else {
            control = this.editContext.getStepEditor(result.draggableId);
        }

        try {
            if (control) {
                let fromSeqId = result.source.droppableId;
                let toSeqId = result.destination.droppableId;
                let fromSeqBranch = 0;
                let toSeqBranch = 0;

                if (fromSeqId.indexOf("::") !== -1) {
                    let pieces = fromSeqId.split("::");
                    fromSeqId = pieces[0];
                    fromSeqBranch = Number.parseInt(pieces[1]);
                }

                if (toSeqId.indexOf("::") !== -1) {
                    let pieces = toSeqId.split("::");
                    toSeqId = pieces[0];
                    toSeqBranch = Number.parseInt(pieces[1]);
                }

                let toSeq = this.flow.getCandidateOrRealSequence(toSeqId);
                let fromSeq = this.flow.getCandidateOrRealSequence(fromSeqId);

                if (!toSeq) {
                    throw new Error("Can not find the droppable sequence");
                }

                if (fromSeqId === toSeqId) {
                    // changing order
                    this.props.flow.moveInSequence(
                        control.getFlowStepData(),
                        toSeqId,
                        result.destination.index,
                        fromSeqBranch,
                        toSeqBranch,
                    );
                    StateManager.propertyChanged(toSeq, FlowStepSequence.STEPS_CHANGED);
                } else {
                    if (toSeq instanceof CandidateSequence) {
                        if (!fromSeq) {
                            // dropping on candidate from toolbox
                            this.editContext.addStepToCandidate(control.getFlowStepData(), toSeq);
                            StateManager.propertyChanged(toSeq, FlowStepSequence.STEPS_CHANGED);
                        } else if (fromSeq instanceof FlowStepSequence) {
                            // dropping on candidate from real sequences.
                            this.editContext.moveStepToCandidate(control.getFlowStepData(), fromSeq, toSeq);
                            this.editContext.onStepMoved(fromSeq, toSeq, control.getFlowStepData());
                            StateManager.propertyChanged(toSeq, FlowStepSequence.STEPS_CHANGED);
                            StateManager.propertyChanged(fromSeq, FlowStepSequence.STEPS_CHANGED);
                        }
                    } else if (toSeq instanceof FlowStepSequence) {
                        if (!fromSeq) {
                            // dropping on real sequence from toolbox.
                            this.flow.add(control.getFlowStepData(), toSeq, result.destination.index, toSeqBranch);

                            StateManager.propertyChanged(toSeq, FlowStepSequence.STEPS_CHANGED);
                        } else if (fromSeq instanceof FlowStepSequence) {
                            // dropping on real sequence from real sequences.
                            this.flow.moveToSequence(
                                control.getFlowStepData(),
                                fromSeq,
                                toSeq,
                                result.destination.index,
                                toSeqBranch,
                            );

                            this.editContext.onStepMoved(fromSeq, toSeq, control.getFlowStepData());
                        }
                    }
                }
            }
        } catch (e) {
            this.editContext.notification = {
                message: e.message,
                onSuccess: () => {},
            };
        }

        // if (control) {
        //     this.editContext.setSelection(control);
        // }
        // else {
        //     this.editContext.clearSelection();
        // }
    }
}
